/**
 * OfflineGiftForm — full-page form for recording a cash / check / pledge gift.
 *
 * Replaces the `max-w-md` modal. Two substantive additions beyond the layout:
 * the gift can be tagged to a donor EVENT (the column existed but nothing in this
 * form ever set it), and it can be bound to a donor RECORD via the people picker,
 * so the gift shows up on that donor's giving history instead of being a loose
 * name string.
 */

import React, { useState } from 'react';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DonationMethod, OfflineDonationCreate } from '@/services/api/donations';
import { DonorEvent } from '@/services/api/donorEvents';
import { PersonResult } from '@/services/api/donorProfiles';
import DonorPeoplePicker from '@/components/donors/DonorPeoplePicker';

interface Props {
  events: DonorEvent[];
  saving: boolean;
  onCancel: () => void;
  onSubmit: (data: OfflineDonationCreate) => void;
}

export const OfflineGiftForm: React.FC<Props> = ({ events, saving, onCancel, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<DonationMethod>('check');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [campaign, setCampaign] = useState('');
  const [eventId, setEventId] = useState('');
  const [notes, setNotes] = useState('');
  const [taxDeductible, setTaxDeductible] = useState(true);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [linked, setLinked] = useState<PersonResult | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  const canSave = parseFloat(amount) > 0 && !saving;

  const field =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
  const label = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Record a gift</h1>
          <p className="text-sm text-gray-500">Cash, check, or pledge. It settles immediately.</p>
        </div>
      </div>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label}>Amount</label>
            <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className={field} placeholder="0" />
          </div>
          <div>
            <label className={label}>Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value as DonationMethod)} className={field}>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="offline">Pledge / offline</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Donor</h2>
        {linked ? (
          <div className="flex items-center justify-between rounded border border-blue-100 bg-blue-50/60 px-3 py-2 dark:border-blue-900 dark:bg-blue-900/20">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{linked.name}</div>
              <div className="truncate text-xs text-gray-500">{linked.email || 'No email on record'}</div>
            </div>
            <button onClick={() => setLinked(null)} className="text-xs text-gray-500 hover:underline">
              Unlink
            </button>
          </div>
        ) : showPicker ? (
          <DonorPeoplePicker
            multiple={false}
            addLabel="Link donor"
            onAdd={(people) => {
              const p = people[0];
              if (!p) return;
              setLinked(p);
              setDonorName(p.name);
              if (p.email) setDonorEmail(p.email);
              setShowPicker(false);
            }}
          />
        ) : (
          <Button size="sm" variant="outline" onClick={() => setShowPicker(true)}>
            Link to a donor record
          </Button>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label}>Donor name</label>
            <input value={donorName} onChange={(e) => setDonorName(e.target.value)} className={field} placeholder="Jane Donor" />
          </div>
          <div>
            <label className={label}>Donor email</label>
            <input type="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} className={field} placeholder="jane@example.org" />
            <p className="mt-1 text-xs text-gray-400">Required to email a receipt.</p>
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Attribution</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label}>Event</label>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} className={field}>
              <option value="">Not tied to an event</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">Rolls up into that event&apos;s total raised.</p>
          </div>
          <div>
            <label className={label}>Campaign</label>
            <input value={campaign} onChange={(e) => setCampaign(e.target.value)} className={field} placeholder="Annual Fund 2026" />
          </div>
        </div>
        <div>
          <label className={label}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={field} placeholder="Check number, restrictions, context…" />
        </div>
      </section>

      <section className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Receipt</h2>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={taxDeductible} onChange={(e) => setTaxDeductible(e.target.checked)} />
          Tax-deductible contribution
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />
          Email a 501(c)(3) receipt now
        </label>
        {sendReceipt && !donorEmail.trim() ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Without an email the receipt is still issued and downloadable — it just waits in the receipts queue until an
            address is added.
          </p>
        ) : null}
      </section>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          onClick={() =>
            onSubmit({
              amount: parseFloat(amount) || 0,
              method,
              donor_name: donorName.trim() || undefined,
              donor_email: donorEmail.trim() || undefined,
              donor_member_id: linked?.id,
              campaign: campaign.trim() || undefined,
              event_id: eventId ? Number(eventId) : undefined,
              notes: notes.trim() || undefined,
              is_tax_deductible: taxDeductible,
              send_receipt: sendReceipt,
            })
          }
        >
          {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Plus size={15} className="mr-1" />}
          Record gift
        </Button>
      </div>
    </div>
  );
};

export default OfflineGiftForm;
